/**
 * GroupAgentsSection Component
 * 
 * Manages agents linked to a group with ability to add/remove
 * Uses EntitySelector for adding agents and displays linked agents with pagination
 * 
 * @component
 */

import { FC, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  FiCpu,
  FiPlus,
  FiTrash2,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { groupService } from '@/services/groupService';
import { useEntitySelection } from '@/hooks';
import { useToast } from '@/hooks/useToast';
import DataTable from '@/components/DataTable/DataTable';
import Button from '@/components/Common/Button';
import Modal from '@/components/Common/Modal';
import ConfirmDialog from '@/components/Common/ConfirmDialog';
import type { AgentListItem } from '@/types';
import type { GroupAgentsSectionProps } from './GroupAgentsSection.types';
import { agentSelectionColumns } from './agentSelectionColumns';
import styles from './GroupAgentsSection.module.css';

/**
 * Get status badge class
 */
const getStatusBadgeClass = (status?: string): string => {
  const statusMap: Record<string, string> = {
    online: styles.statusOnline,
    offline: styles.statusOffline,
    inactive: styles.statusInactive,
    error: styles.statusError,
  };
  return statusMap[status || 'offline'] || styles.statusOffline;
};

/**
 * Get status icon
 */
const getStatusIcon = (status?: string) => {
  const iconProps = { size: 14 };
  const statusIcons: Record<string, JSX.Element> = {
    online: <FiCheckCircle {...iconProps} />,
    offline: <FiXCircle {...iconProps} />,
    inactive: <FiAlertCircle {...iconProps} />,
    error: <FiAlertCircle {...iconProps} />,
  };
  return statusIcons[status || 'offline'] || <FiXCircle {...iconProps} />;
};



const GroupAgentsSection: FC<GroupAgentsSectionProps> = ({
  groupId,
  onUpdate,
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [linkedAgentsPage, setLinkedAgentsPage] = useState(1);
  const linkedAgentsPageSize = 10;
  const [selectedAgents, setSelectedAgents] = useState<AgentListItem[]>([]);
  
  // Confirm dialog state
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [agentToRemove, setAgentToRemove] = useState<{ id: string; name: string } | null>(null);
  
  const queryClient = useQueryClient();
  const toast = useToast();

  /**
   * Fetch linked agents (paginated)
   */
  const { data: linkedAgentsData, isLoading: isLoadingLinked } = useQuery({
    queryKey: ['group-agents', groupId, linkedAgentsPage],
    queryFn: async () => {
      return groupService.getGroupAgents(groupId, {
        page: linkedAgentsPage - 1, // API is 0-indexed
        size: linkedAgentsPageSize,
      });
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const linkedAgents = linkedAgentsData?.data || [];
  const linkedAgentsPagination = linkedAgentsData?.pagination;

  /**
   * Entity selection for available agents (not linked to group)
   * Uses the new endpoint that returns only agents NOT in the group
   */
  const agentSelection = useEntitySelection<AgentListItem>({
    fetchFn: (params) => groupService.getAvailableAgents(groupId, params),
    queryKey: `available-agents-${groupId}`,
    initialPageSize: 10,
    searchField: 'hostname', // Search by hostname specifically
  });

  /**
   * Link agents mutation
   */
  const linkMutation = useMutation({
    mutationFn: async (agentIds: string[]) => {
      // Link all selected agents
      await Promise.all(
        agentIds.map((agentId) => groupService.linkAgent(groupId, agentId))
      );
    },
    onSuccess: () => {
      toast.success(
        'Agents vinculados!',
        'Os agents foram vinculados ao grupo com sucesso.'
      );
      
      // Reset selection and close modal
      setSelectedAgents([]);
      agentSelection.clearSelection();
      setIsAddModalOpen(false);
      
      // Invalidate both linked agents AND available agents cache
      queryClient.invalidateQueries({ queryKey: ['group-agents', groupId] });
      queryClient.invalidateQueries({ queryKey: [`available-agents-${groupId}`] });
      
      // Force refetch of available agents to update the list immediately
      agentSelection.refetch();
      
      if (onUpdate) {
        onUpdate();
      }
    },
    onError: (error) => {
      toast.error(
        'Erro ao vincular agents',
        error instanceof Error ? error.message : 'Erro desconhecido'
      );
    },
  });

  /**
   * Unlink agent mutation
   */
  const unlinkMutation = useMutation({
    mutationFn: async (agentId: string) => {
      await groupService.unlinkAgent(groupId, agentId);
    },
    onSuccess: () => {
      toast.success(
        'Agent removido!',
        'O agent foi removido do grupo com sucesso.'
      );
      
      // Invalidate both linked agents AND available agents cache
      // When we unlink, the agent becomes available again
      queryClient.invalidateQueries({ queryKey: ['group-agents', groupId] });
      queryClient.invalidateQueries({ queryKey: [`available-agents-${groupId}`] });
      
      if (onUpdate) {
        onUpdate();
      }
    },
    onError: (error) => {
      toast.error(
        'Erro ao remover agent',
        error instanceof Error ? error.message : 'Erro desconhecido'
      );
    },
  });

  /**
   * Handle add agents
   */
  const handleAddAgents = () => {
    if (selectedAgents.length === 0) {
      toast.info(
        'Nenhum agent selecionado',
        'Selecione pelo menos um agent para vincular.'
      );
      return;
    }
    
    const agentIds = selectedAgents.map(agent => agent.id);
    linkMutation.mutate(agentIds);
  };

  /**
   * Handle open add modal
   * Refetch available agents to ensure fresh data
   */
  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
    setSelectedAgents([]);
    // Refetch available agents when modal opens to ensure fresh data
    agentSelection.refetch();
  };

  /**
   * Handle remove agent - Open confirm dialog
   */
  const handleRemoveAgent = (agentId: string, agentName: string) => {
    setAgentToRemove({ id: agentId, name: agentName });
    setIsConfirmOpen(true);
  };

  /**
   * Handle confirm remove
   */
  const handleConfirmRemove = () => {
    if (agentToRemove) {
      unlinkMutation.mutate(agentToRemove.id);
      setIsConfirmOpen(false);
      setAgentToRemove(null);
    }
  };

  /**
   * Handle cancel remove
   */
  const handleCancelRemove = () => {
    setIsConfirmOpen(false);
    setAgentToRemove(null);
  };

  return (
    <div className={styles.groupAgentsSection}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.icon}>
            <FiCpu size={20} />
          </div>
          <div>
            <h2 className={styles.title}>Agents Vinculados</h2>
            <p className={styles.subtitle}>
              {linkedAgentsPagination?.total || 0} {linkedAgentsPagination?.total === 1 ? 'agent vinculado' : 'agents vinculados'}
            </p>
          </div>
        </div>
        <Button
          variant="primary"
          size="md"
          iconLeft={<FiPlus size={16} />}
          onClick={handleOpenAddModal}
        >
          Adicionar Agents
        </Button>
      </div>

      {/* Linked Agents List */}
      <div className={styles.agentsList}>
        {isLoadingLinked ? (
          <div className={styles.loadingState}>
            <p>Carregando agents vinculados...</p>
          </div>
        ) : linkedAgents.length === 0 ? (
          <div className={styles.emptyState}>
            <FiCpu size={48} className={styles.emptyIcon} />
            <p className={styles.emptyMessage}>Nenhum agent vinculado a este grupo</p>
            <p className={styles.emptyHint}>Clique em "Adicionar Agents" para começar</p>
          </div>
        ) : (
          <>
            {/* Agents Table */}
            <div className={styles.tableWrapper}>
              <table className={styles.agentsTable}>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Hostname</th>
                    <th>Sistema Operacional</th>
                    <th>Status</th>
                    <th style={{ width: '80px', textAlign: 'center' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedAgents.map((agent) => (
                    <tr key={agent.id}>
                      <td>
                        <div className={styles.agentNameCell}>
                          <FiCpu size={16} className={styles.agentIcon} />
                          <span className={styles.agentName}>{agent.name}</span>
                        </div>
                      </td>
                      <td>{agent.hostname || '-'}</td>
                      <td>{agent.os ? `${agent.os} ${agent.osVersion || ''}`.trim() : '-'}</td>
                      <td>
                        {agent.status && (
                          <span className={`${styles.statusBadge} ${getStatusBadgeClass(agent.status)}`}>
                            {getStatusIcon(agent.status)}
                            {agent.status}
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className={styles.removeButton}
                          onClick={() => handleRemoveAgent(agent.id, agent.name)}
                          disabled={unlinkMutation.isPending}
                          title="Remover agent"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination for linked agents */}
            {linkedAgentsPagination && linkedAgentsPagination.totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  onClick={() => setLinkedAgentsPage((prev) => Math.max(1, prev - 1))}
                  disabled={!linkedAgentsPagination.hasPrev}
                  className={styles.paginationButton}
                >
                  <FiChevronLeft size={16} />
                  Anterior
                </button>
                <span className={styles.paginationInfo}>
                  Página {linkedAgentsPage} de {linkedAgentsPagination.totalPages}
                </span>
                <button
                  onClick={() => setLinkedAgentsPage((prev) => Math.min(linkedAgentsPagination.totalPages, prev + 1))}
                  disabled={!linkedAgentsPagination.hasNext}
                  className={styles.paginationButton}
                >
                  Próxima
                  <FiChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Agents Modal */}
      <Modal
        open={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedAgents([]);
          agentSelection.clearSelection();
        }}
        title="Adicionar Agents ao Grupo"
        description="Selecione os agents que deseja vincular a este grupo"
        size="lg"
      >
        <div className={styles.modalContent}>
          {/* Search Input */}
          <div className={styles.searchContainer}>
            <input
              type="text"
              placeholder="Buscar por hostname..."
              value={agentSelection.searchTerm}
              onChange={(e) => agentSelection.handleSearch(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          {/* DataTable with Selection */}
          <div className={styles.tableContainer}>
            <DataTable
              data={agentSelection.entities}
              columns={agentSelectionColumns}
              isLoading={agentSelection.isLoading}
              error={agentSelection.error}
              enableRowSelection={true}
              onRowSelectionChange={setSelectedAgents}
              enablePagination={false}
              emptyMessage="Nenhum agent disponível para vincular"
              className={styles.agentSelectionTable}
            />
          </div>

          {/* Custom Pagination Controls */}
          {agentSelection.totalItems > 0 && (
            <div className={styles.paginationContainer}>
              <div className={styles.paginationLeft}>
                {/* Page Size Selector */}
                <div className={styles.pageSizeSelector}>
                  <label className={styles.pageSizeLabel}>
                    Itens por página:
                  </label>
                  <select
                    className={styles.pageSizeSelect}
                    value={agentSelection.pageSize}
                    onChange={(e) => agentSelection.handlePageSizeChange(Number(e.target.value))}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                
                {/* Pagination Info */}
                <div className={styles.paginationInfo}>
                  Mostrando {((agentSelection.currentPage - 1) * agentSelection.pageSize) + 1} a{' '}
                  {Math.min(agentSelection.currentPage * agentSelection.pageSize, agentSelection.totalItems)} de{' '}
                  {agentSelection.totalItems} registros
                </div>
              </div>
              
              {/* Pagination Controls */}
              <div className={styles.paginationControls}>
                <button
                  className={styles.paginationButton}
                  onClick={() => agentSelection.handlePageChange(1)}
                  disabled={agentSelection.currentPage === 1}
                  title="Primeira página"
                >
                  ««
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={() => agentSelection.handlePageChange(agentSelection.currentPage - 1)}
                  disabled={agentSelection.currentPage === 1}
                  title="Página anterior"
                >
                  «
                </button>
                <span className={styles.pageInfo}>
                  Página {agentSelection.currentPage} de {agentSelection.totalPages}
                </span>
                <button
                  className={styles.paginationButton}
                  onClick={() => agentSelection.handlePageChange(agentSelection.currentPage + 1)}
                  disabled={agentSelection.currentPage >= agentSelection.totalPages}
                  title="Próxima página"
                >
                  »
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={() => agentSelection.handlePageChange(agentSelection.totalPages)}
                  disabled={agentSelection.currentPage >= agentSelection.totalPages}
                  title="Última página"
                >
                  »»
                </button>
              </div>
            </div>
          )}
          
          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setIsAddModalOpen(false);
                setSelectedAgents([]);
                agentSelection.clearSelection();
              }}
              disabled={linkMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleAddAgents}
              disabled={linkMutation.isPending || selectedAgents.length === 0}
              loading={linkMutation.isPending}
            >
              Adicionar {selectedAgents.length > 0 && `(${selectedAgents.length})`}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirm Remove Dialog */}
      <ConfirmDialog
        open={isConfirmOpen}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        title="Remover Agent"
        message={`Tem certeza que deseja remover o agent "${agentToRemove?.name}" deste grupo?`}
        confirmText="Remover"
        cancelText="Cancelar"
        variant="danger"
        isLoading={unlinkMutation.isPending}
      />
    </div>
  );
};

export default GroupAgentsSection;
